/* eslint-disable no-nested-ternary */
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import logger from '@adc/logger'
// @ts-ignore next
import analytics from '@adc/analytics'
import classnames from 'classnames/bind'
import { match } from 'ts-pattern'
import * as graph from '@adc/sdk'
import type { Listing } from '@adc/sdk/types'
import Button from '../../elementals/button'
import Icon from '../../elementals/icon'
import Text from '../../elementals/text'
import useBreakpoints from '../../../hooks/useBreakpoints'
import { ListingInfoCardRibbon } from './listingInfoCardRibbon'
import Overlay from './overlay'
import styles from './index.scss'
import {
  VerifiedImage,
  BackgroundImage,
  SecondaryItem,
  MapItem,
  TABS,
} from './parts'
import ImageWatermark from './overlay/imageWatermark'
import { watermarkTypes } from 'src/constants'
import getStreetViewAvailability from '../../../utils/getStreetViewAvailability'

const cx = classnames.bind(styles)

export const LISTING_INFORMATION_PHOTO_WIDTH = 955 // was 750

/**
 * AlternateItems are used to fill positions in showMultiple mode e.g. Map
 */
export enum AlternateItemType {
  MapItem,
  StreetViewItem,
  CustomItem,
}

export type AlternateItem = {
  showAlways?: boolean // if false, it will only show if there's no photo available for it
  itemType?: AlternateItemType
  renderer?: () => JSX.Element // required if itemType is CustomItem
}

const getCommonTrackingValues = (listing: Listing) => {
  const { morpheusListingId, morpheusPropertyId } =
    analytics.standardTracking.listing(listing)
  return {
    morpheusListingId,
    morpheusPropertyId,
  }
}

const trackEvent = (event: string, listing: Listing) => {
  analytics.track(event, getCommonTrackingValues(listing))
}

const defaultAlternateItems: AlternateItem[] = [
  {
    itemType: AlternateItemType.MapItem,
    showAlways: true,
  },
  {
    itemType: AlternateItemType.StreetViewItem,
    showAlways: true,
  },
  {
    itemType: AlternateItemType.CustomItem,
    showAlways: true,
    renderer: () => (
      <div
        style={{
          borderRadius: 6,
          display: 'flex',
          background: 'lightgrey',
          height: '100%',
          width: '100%',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Text color='gray' weight='medium'>
          Custom Item Goes Here (TODO)
        </Text>
      </div>
    ),
  },
]

interface PhotoGalleryProps {
  config: Record<string, unknown>
  className?: string
  cls?: Record<string, string>
  displayBanner: boolean
  shouldShowRibbon?: boolean
  children?: React.ReactNode
  featureFlags?: Record<string, boolean>
  listing: Listing
  fluid?: boolean
  showMultiple?: boolean
  alternativeItems?: AlternateItem[]
  supportLargeDesktop?: boolean
  watermarkType?: keyof typeof watermarkTypes
  defaultPhotoConfigKey?: string
}

const PhotoGallery = ({
  config,
  className,
  cls = {},
  displayBanner = false,
  shouldShowRibbon = false,
  children,
  featureFlags = {},
  listing,
  fluid = false,
  showMultiple = false,
  alternativeItems = defaultAlternateItems,
  supportLargeDesktop = true,
  watermarkType,
  defaultPhotoConfigKey,
}: PhotoGalleryProps) => {
  const [images, setImages] = useState<string[]>([])
  const breakpoint = useBreakpoints()
  const isMobile = breakpoint === 'mobile'
  useEffect(() => {
    if (!listing?.photos?.length) {
      setImages([])
    } else {
      setImages(
        listing?.photos
          ?.map(photo => photo?.file_descriptor?.urls?.cdn)
          .filter((url): url is string => !!url)
      )
    }
  }, [listing.photos])

  const addressTuple: string[] = useMemo(
    () =>
      listing.formatted_address?.filter(
        (address): address is string => !!address
      ) || ['', ''],
    [listing.formatted_address]
  )
  const carouselRef = useRef<HTMLDivElement>(null)
  const [activeTabIndex, setActiveTabIndex] = useState<
    (typeof TABS)[keyof typeof TABS]
  >(TABS.NONE)
  const [streetViewAvailable, setStreetViewAvailable] = useState(false)
  // Calculate coordinates from listing
  const coords = useMemo(() => {
    const { lat, lon } =
      listing?.primary_property?.summary?.address?.coordinates || {}
    if (
      lat !== undefined &&
      lat !== null &&
      lon !== undefined &&
      lon !== null
    ) {
      return { lat, lng: lon }
    }
    return undefined
  }, [listing?.primary_property?.summary?.address?.coordinates])

  const toggleLock = (open = true) => {
    if (typeof window === 'undefined') return

    const method = open ? 'add' : 'remove'
    document.documentElement.classList[method](cx('body-noscroll'))
  }
  // Check Street View availability whenever coords change
  useEffect(() => {
    ;(async () => {
      if (coords) {
        try {
          const available = await getStreetViewAvailability(coords)
          setStreetViewAvailable(available)
        } catch (error) {
          logger.info(
            'Unhandled error in StreetView availability check:',
            error
          )
          setStreetViewAvailable(false)
        }
      }
    })().catch(error => {
      logger.info('Unhandled error in StreetView availability check:', error)
    })
  }, [coords])
  const setActiveTab = useCallback(
    (activeTabIndex: (typeof TABS)[keyof typeof TABS]) => {
      switch (activeTabIndex) {
        case TABS.PHOTOS:
          trackEvent('pdp-gallery-photos-button', listing)
          break
        case TABS.STREET:
          trackEvent('pdp-gallery-street-button', listing)
          break
        case TABS.MAP:
          trackEvent('pdp-gallery-map-button', listing)
          break
        default:
          break
      }
      setActiveTabIndex(activeTabIndex)
      toggleLock(true)
    },
    []
  )

  const onClose = useCallback(() => {
    setActiveTabIndex(null)
    toggleLock(false)
  }, [])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // prevent buttons from being clicked when pressing enter
      if (event.key === 'Enter') {
        event.preventDefault()
      }
      if (activeTabIndex === null) return
      if (event.key === 'Escape') {
        onClose()
      }
    },
    [activeTabIndex, onClose]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleKeyDown])

  // TODO: fix graph to send "Auction in Progress" for AUCTION_IN_PROGRESS
  const statusLabel =
    listing.listing_status === graph.enums.ListingStatus.AuctionInProgress
      ? 'Auction In Progress'
      : listing.listing_status_label
  const statusLabelRole = useMemo(() => {
    return match(listing)
      .with({ is_coming_soon: true }, () => ({
        color: 'success',
        icon: 'house',
      }))
      .with(
        { listing_status_group: graph.enums.ListingStatusGroup.Active },
        () => ({ color: 'success', icon: 'gavel' })
      )
      .with(
        { listing_status_group: graph.enums.ListingStatusGroup.Pending },
        () => ({ color: 'warning', icon: 'house-circle-check' })
      )
      .with(
        { listing_status_group: graph.enums.ListingStatusGroup.Sold },
        () => ({ color: 'warning', icon: 'house-circle-check' })
      )
      .with(
        { listing_status_group: graph.enums.ListingStatusGroup.Inactive },
        () => ({
          color: 'warning',
          icon: 'house',
        })
      )
      .otherwise(() => ({ statusGroup: '', color: '', icon: '' }))
  }, [listing])

  const videoUrlForThisProperty =
    listing.primary_property_id &&
    typeof featureFlags['fe.resi.property-video-config'] === 'object'
      ? (featureFlags['fe.resi.property-video-config']?.[
          listing.primary_property_id
        ] as string)
      : undefined

  const videoButtonClicked = () => {
    analytics.track({ eventName: 'video_button_clicked' })
  }

  const mapButton = useMemo(
    () => (
      <Button
        icon
        size='small'
        elmId='property_gallery_ctrl_map'
        onClick={() => setActiveTab(TABS.MAP)}
      >
        <Icon name='map-marked' iconStyle='light' size='large' /> Map
      </Button>
    ),
    [setActiveTab]
  )

  const wontDisplayMultiple =
    !showMultiple ||
    ((breakpoint === 'mobile' || breakpoint === 'tablet') && showMultiple)

  const renderButtons = useCallback(
    () => (
      <>
        <div className={cx('left-button-group')}>
          {images.length > 1 && (
            <Button
              icon
              size='small'
              elmId='property_gallery_ctrl_image'
              onClick={() => setActiveTab(TABS.PHOTOS)}
            >
              <Icon name='image' iconStyle='light' size='large' />{' '}
              {images.length} Photos
            </Button>
          )}
          {videoUrlForThisProperty && (
            <Button
              icon
              size='small'
              className={cx('ctrl-video')}
              elmId='property_gallery_ctrl_video'
              link={videoUrlForThisProperty}
              target='_blank'
              onClick={videoButtonClicked}
            >
              <Icon name='video' iconStyle='light' size='large' />
            </Button>
          )}
        </div>
        {coords && wontDisplayMultiple && (
          <div className={cx('right-button-group')}>
            {mapButton}
            {streetViewAvailable && (
              <Button
                icon
                size='small'
                elmId='property_gallery_ctrl_street'
                onClick={() => setActiveTab(TABS.STREET)}
              >
                <Icon name='street-view' iconStyle='light' size='large' />{' '}
                Street
              </Button>
            )}
          </div>
        )}
      </>
    ),
    [
      coords,
      images.length,
      mapButton,
      setActiveTab,
      streetViewAvailable,
      videoUrlForThisProperty,
      wontDisplayMultiple,
    ]
  )

  /**
   * START: showMultiple features here...
   */

  const onSecondaryPhotoClick = useCallback(
    (index, enabled) => {
      if (!enabled) return
      setActiveTab(TABS.PHOTOS)
      setTimeout(() => {
        if (carouselRef.current) {
          // @ts-ignore next
          carouselRef.current.slickGoTo(index) // eslint-disable-line @typescript-eslint/no-unsafe-call
        }
      }, 10)
    },
    [setActiveTab]
  )

  interface SecondaryPhotoProps {
    index: number
    keyOverride?: number
    url?: string
    featureFlags: Record<string, boolean>
    className?: string
  }

  /**
   * This holds an actual secondary photo (not for other items)
   */
  const SecondaryPhoto = useCallback(
    ({ index, url, featureFlags, className }: SecondaryPhotoProps) => (
      <BackgroundImage
        className={cx(
          'secondary-photo',
          `index-${index}`,
          { pointer: Boolean(url) },
          className
        )}
        data-elm-id={`secondary_photo_${index}`}
        onClick={() => onSecondaryPhotoClick(index, Boolean(url))}
        featureFlags={featureFlags}
        url={url}
        defaultPhotoConfigKey={defaultPhotoConfigKey}
      />
    ),
    [onSecondaryPhotoClick, defaultPhotoConfigKey]
  )

  const hasMap = Boolean(coords)

  const renderSecondaryPhotoOrPrimary = useCallback(
    (correspondingImageIndex: number) => {
      const hasEnoughImages = images.length > correspondingImageIndex
      if (hasEnoughImages)
        return (
          <SecondaryPhoto
            key={correspondingImageIndex}
            index={correspondingImageIndex}
            url={images[correspondingImageIndex]}
            featureFlags={featureFlags}
          />
        )
      // In case there is not enough photos for all chiclets, we need to display the default "Photo coming soon photo"
      return (
        <SecondaryPhoto
          index={1}
          key={correspondingImageIndex}
          featureFlags={featureFlags}
        />
      )
    },
    [SecondaryPhoto, featureFlags, images]
  )

  /**
   * Returns the element for the alternate item.
   * MapItem and StreetViewItem are special cases the we handle here.
   * Otherwise, it's a custom item with a custom render function.
   */
  const getItemElement = useCallback(
    (item: AlternateItem, index: number) => {
      return match(item)
        .with(
          { itemType: AlternateItemType.MapItem },
          { itemType: AlternateItemType.StreetViewItem },
          item =>
            hasMap ? (
              <MapItem
                defaultView={
                  item.itemType === AlternateItemType.StreetViewItem
                    ? renderSecondaryPhotoOrPrimary(index)
                    : undefined
                }
                key={index}
                index={index}
                coords={coords}
                setActiveTab={setActiveTab}
                showStreetView={
                  item.itemType === AlternateItemType.StreetViewItem
                }
              />
            ) : (
              renderSecondaryPhotoOrPrimary(index)
            )
        )
        .with({ itemType: AlternateItemType.CustomItem }, item => {
          if (!item.renderer) {
            throw new Error('Custom item must have a renderer')
          }
          return (
            <SecondaryItem key={index} index={index} renderer={item.renderer} />
          )
        })
        .otherwise(() => {
          throw new Error('Unknown item type')
        })
    },
    [coords, hasMap, renderSecondaryPhotoOrPrimary, setActiveTab]
  )

  /**
   * Returns an array of elements that are filled with altItems where needed or required
   *
   * For 2 positions, we fill #2 then #1, with the altItems
   *
   * |------------------|---------|
   * |                  |         |
   * |                  |    1    |
   * |                  |         |
   * |        0         |---------|
   * |                  |         |
   * |                  |    2    |
   * |                  |         |
   * |------------------|---------|
   *
   * For 4 positions, we fill #4, #3, #2, #1, with the altItems
   *
   * |------------------|---------|---------|
   * |                  |         |         |
   * |                  |    1    |    3    |
   * |                  |         |         |
   * |        0         |---------|---------|
   * |                  |         |         |
   * |                  |    2    |    4    |
   * |                  |         |         |
   * |------------------|---------|---------|
   *
   *  Could easily be extended to handle 6, 8 etc. by just changing the countNeeded type
   */
  const getItemElements = useCallback(
    (countNeeded: 2 | 4) => {
      let position = countNeeded // current position to fill, e.g. (4, 3, 2, 1) or (2, 1)
      const sourceAlternativeItems = [...alternativeItems]
      const newItemElements: JSX.Element[] = []

      let altItem = sourceAlternativeItems.shift() // pull off the top of queue
      while (altItem && newItemElements.length < countNeeded) {
        // if showAlways or if there are not enough images to fill that position, then fill with alt
        if (altItem.showAlways || images.length <= position) {
          // images are 1-based due to primary
          newItemElements.unshift(getItemElement(altItem, position))
        } else {
          // use photo
          newItemElements.unshift(renderSecondaryPhotoOrPrimary(position))
        }
        position--
        altItem = sourceAlternativeItems.shift()
      }

      // fill the rest with photos
      while (newItemElements.length < countNeeded) {
        // fill with photo that belongs there
        newItemElements.unshift(renderSecondaryPhotoOrPrimary(position))
        position--
      }
      return newItemElements
    },
    [
      alternativeItems,
      getItemElement,
      images.length,
      renderSecondaryPhotoOrPrimary,
    ]
  )

  const getSecondaryItems = useMemo(() => {
    const items = match<typeof breakpoint, JSX.Element[]>(breakpoint)
      .with('mobile', () => [])
      .with('tablet', () => [])
      .with('laptop', () => getItemElements(2))
      .otherwise(() => getItemElements(4))
    return items
  }, [breakpoint, getItemElements])

  /**
   * END: showMultiple-specific code
   */

  const PrimaryOverlay = ({
    watermarkType,
  }: {
    watermarkType?: keyof typeof watermarkTypes
  }) => (
    <>
      <>
        {renderButtons()}
        {isMobile && statusLabel && displayBanner && (
          <div
            className={cx('status-label', statusLabelRole.color)}
            data-elm-id='property_gallery_status_label'
          >
            <Text color='white' weight='medium'>
              {statusLabel}
            </Text>
          </div>
        )}
        <ImageWatermark type={watermarkType} />
      </>
    </>
  )

  const PrimaryPhoto = () => {
    return (
      <>
        <div className={cx('image-container', { pointer: images.length > 0 })}>
          <VerifiedImage
            className={cx(cls.image, 'image')}
            data-elm-id='property_gallery_image'
            alt={`${addressTuple[0]} ${addressTuple[1]}`}
            onClick={
              images.length > 0 ? () => setActiveTab(TABS.PHOTOS) : () => null
            }
            url={images[0]}
            featureFlags={featureFlags}
            defaultPhotoConfigKey={defaultPhotoConfigKey}
          />
        </div>

        <PrimaryOverlay watermarkType={watermarkType} />
      </>
    )
  }

  return (
    <>
      {shouldShowRibbon && !isMobile && (
        <ListingInfoCardRibbon
          listing={listing}
          statusLabelRole={statusLabelRole}
        />
      )}

      <div
        className={cx('root', className, { fluid, multiple: showMultiple })}
        data-elm-id='property_gallery_section'
      >
        {!showMultiple || breakpoint === 'mobile' ? (
          <PrimaryPhoto />
        ) : (
          <div
            className={cx('multi-container', {
              'large-desktop-support': supportLargeDesktop,
            })}
          >
            <div
              className={cx('primary-photo', { pointer: images.length > 0 })}
            >
              <BackgroundImage
                key={0}
                className={cx('primary-photo')}
                data-elm-id='property_gallery_image'
                onClick={
                  images.length > 0
                    ? () => setActiveTab(TABS.PHOTOS)
                    : () => undefined
                }
                featureFlags={featureFlags}
                url={images?.[0] || undefined}
              />
              <PrimaryOverlay watermarkType={watermarkType} />
            </div>
            <div className={cx('secondary-photos')}>{getSecondaryItems}</div>
          </div>
        )}

        {activeTabIndex !== null && (
          <Overlay
            config={config}
            // @ts-ignore next
            coords={coords}
            images={images}
            addressTuple={addressTuple}
            activeTabIndex={activeTabIndex}
            onTabChange={setActiveTab}
            onSave={() => undefined}
            onClose={onClose}
            featureFlags={featureFlags}
            carouselRef={carouselRef}
          >
            {/* @ts-ignore next */}
            {children}
          </Overlay>
        )}
      </div>
    </>
  )
}

export default PhotoGallery
